import axios from "axios";
import { toast } from "react-toastify";

import Urls from '../../Utilities/General/Urls'
import { getToken } from '../../Utilities/Auth/AuthPermissions'
import { error, networkError, serverError, errorTimeout, expiredSession } from '../../Utilities/General/StatusHandler';

const paymentLinkErrorMessage = responseData => {
    if (responseData?.detail) return responseData.detail
    if (!responseData || typeof responseData !== 'object') return null

    const fieldLabels = {
        manual_link_reason: 'Motivo del enlace manual',
        license_id: 'Licencia',
        pricing_ids: 'Productos de licencia',
        tiendanube_order_id: 'ID técnico de orden Tienda Nube',
        tiendanube_transaction_id: 'ID de transacción Tienda Nube'
    }
    const messages = Object.entries(responseData).flatMap(([field, errors]) => {
        const value = Array.isArray(errors) ? errors.join(' ') : String(errors)
        return value ? [`${fieldLabels[field] || field}: ${value}`] : []
    })
    return messages.join(' ')
}

const UsoundPaymentsService = {

    getProducts: async () => {
        try {
            const { data } = await axios.get(
                Urls.urlUsoundPayProducts,
                { headers: { Authorization: getToken() } }
            )
            return data
        } catch (err) {
            if (err.message.includes('Network Error')) return networkError()
            if (err.message.includes('500')) return serverError()
            if (err.message.includes('504')) return errorTimeout()
            if (err.message.includes('401') && !toast.isActive('expiredSessionToast')) return expiredSession()
        }
    },

    getOrders: async () => {
        try {
            const { data } = await axios.get(
                Urls.urlUsoundPayOrder,
                { headers: { Authorization: getToken() } }
            )
            return data
        } catch (err) {
            if (err.message.includes('Network Error')) return networkError()
            if (err.message.includes('500')) return serverError()
            if (err.message.includes('504')) return errorTimeout()
            if (err.message.includes('401') && !toast.isActive('expiredSessionToast')) return expiredSession()
        }
    },

    updateOrder: async order => {
        try {
            const { data } = await axios.put(
                Urls.urlUsoundPayOrder + `${order.id}/`,
                order,
                { headers: { Authorization: getToken() } }
            )
            return data
        } catch (err) {
            if (err.message.includes('Network Error')) return networkError()
            if (err.message.includes('500')) return serverError()
            if (err.message.includes('504')) return errorTimeout()
            if (err.message.includes('400')) return error()
            if (err.message.includes('401') && !toast.isActive('expiredSessionToast')) return expiredSession()
        }
    },

    deleteOrder: async order => {
        try {
            await axios.delete(
                Urls.urlUsoundPayOrder + `${order.id}/`,
                { headers: { Authorization: getToken() } }
            )
        } catch (err) {
            if (err.message.includes('Network Error')) return networkError()
            if (err.message.includes('500')) return serverError()
            if (err.message.includes('504')) return errorTimeout()
            if (err.message.includes('401') && !toast.isActive('expiredSessionToast')) return expiredSession()
        }
    },

    saveNewOrder: async newOrder => {
        try {
            const { data } = await axios.post(
                Urls.urlUsoundPayOrder,
                newOrder,
                { headers: { Authorization: getToken() } }
            )
            return data
        } catch (err) {
            if (err.message.includes('Network Error')) return networkError()
            if (err.message.includes('500')) return serverError()
            if (err.message.includes('504')) return errorTimeout()
            if (err.message.includes('401') && !toast.isActive('expiredSessionToast')) return expiredSession()
        }
    },

    linkTiendaNubePayment: async paymentLink => {
        const diagnosticContext = {
            licenseId: paymentLink.license_id,
            pricingIds: paymentLink.pricing_ids,
            manualLinkReason: paymentLink.manual_link_reason,
            tiendanubeOrderId: paymentLink.tiendanube_order_id,
            tiendanubeTransactionId: paymentLink.tiendanube_transaction_id || null
        }
        console.info('[tiendanube-link] request started', diagnosticContext)
        try {
            const { data } = await axios.post(
                Urls.urlUsoundPayTiendaNubePaymentLink,
                paymentLink,
                { headers: { Authorization: getToken() } }
            )
            console.info('[tiendanube-link] request succeeded', {
                ...diagnosticContext,
                externalPaymentId: data.id,
                created: data.created
            })
            return data
        } catch (err) {
            console.error('[tiendanube-link] request failed', {
                ...diagnosticContext,
                httpStatus: err.response?.status,
                response: err.response?.data,
                message: err.message
            })
            if (err.message.includes('Network Error')) {
                networkError()
                throw err
            }
            if (err.message.includes('500')) {
                serverError()
                throw err
            }
            if (err.message.includes('504')) {
                errorTimeout()
                throw err
            }
            if (err.message.includes('401')) {
                if (!toast.isActive('expiredSessionToast')) expiredSession()
                throw err
            }
            throw new Error(paymentLinkErrorMessage(err.response?.data) || 'No se pudo verificar el pago de Tienda Nube.')
        }
    },

    createRebillLicenseLink: async paymentLink => {
        try {
            const { data } = await axios.post(
                Urls.urlUsoundPayRebillLicenseLink,
                paymentLink,
                { headers: { Authorization: getToken() } }
            )
            return data
        } catch (err) {
            if (err.message.includes('Network Error')) {
                networkError()
                throw err
            }
            if (err.message.includes('500')) {
                serverError()
                throw err
            }
            if (err.message.includes('504')) {
                errorTimeout()
                throw err
            }
            if (err.message.includes('401')) {
                if (!toast.isActive('expiredSessionToast')) expiredSession()
                throw err
            }
            throw new Error(paymentLinkErrorMessage(err.response?.data) || 'No se pudo crear el link de pago de Rebill.')
        }
    },

    getRebillLicenseLinks: async licenseId => {
        try {
            const { data } = await axios.get(
                Urls.urlUsoundPayRebillLicenseLinks,
                {
                    headers: { Authorization: getToken() },
                    params: { license_id: licenseId }
                }
            )
            return data?.results || []
        } catch (err) {
            if (err.message.includes('Network Error')) {
                networkError()
                throw err
            }
            if (err.message.includes('500')) {
                serverError()
                throw err
            }
            if (err.message.includes('504')) {
                errorTimeout()
                throw err
            }
            if (err.message.includes('401')) {
                if (!toast.isActive('expiredSessionToast')) expiredSession()
                throw err
            }
            throw new Error(paymentLinkErrorMessage(err.response?.data) || 'No se pudieron consultar las operaciones de Rebill.')
        }
    },

    createStripeLicenseLink: async paymentLink => {
        try {
            const { data } = await axios.post(
                Urls.urlUsoundPayStripeLicenseLink,
                paymentLink,
                { headers: { Authorization: getToken() } }
            )
            return data
        } catch (err) {
            if (err.message.includes('Network Error')) {
                networkError()
                throw err
            }
            if (err.message.includes('500')) {
                serverError()
                throw err
            }
            if (err.message.includes('504')) {
                errorTimeout()
                throw err
            }
            if (err.message.includes('401')) {
                if (!toast.isActive('expiredSessionToast')) expiredSession()
                throw err
            }
            throw new Error(paymentLinkErrorMessage(err.response?.data) || 'No se pudo crear el link de pago de Stripe.')
        }
    },

    getStripeLicenseLinks: async licenseId => {
        try {
            const { data } = await axios.get(
                Urls.urlUsoundPayStripeLicenseLinks,
                {
                    headers: { Authorization: getToken() },
                    params: { license_id: licenseId }
                }
            )
            return data?.results || []
        } catch (err) {
            if (err.message.includes('Network Error')) {
                networkError()
                throw err
            }
            if (err.message.includes('500')) {
                serverError()
                throw err
            }
            if (err.message.includes('504')) {
                errorTimeout()
                throw err
            }
            if (err.message.includes('401')) {
                if (!toast.isActive('expiredSessionToast')) expiredSession()
                throw err
            }
            throw new Error(paymentLinkErrorMessage(err.response?.data) || 'No se pudieron consultar las operaciones de Stripe.')
        }
    },

    deleteOrderDetail: async id => {
        try {
            await axios.delete(
                Urls.urlUsoundPayOrderDetail + `${id}/`,
                { headers: { Authorization: getToken() } }
            )
        } catch (err) {
            if (err.message.includes('Network Error')) return networkError()
            if (err.message.includes('500')) return serverError()
            if (err.message.includes('504')) return errorTimeout()
            if (err.message.includes('401') && !toast.isActive('expiredSessionToast')) return expiredSession()
        }
    },

    getCustomers: async () => {
        try {
            const { data } = await axios.get(
                Urls.urlUsoundPayCustomer,
                { headers: { Authorization: getToken() } }
            )
            return data
        } catch (err) {
            if (err.message.includes('Network Error')) return networkError()
            if (err.message.includes('500')) return serverError()
            if (err.message.includes('504')) return errorTimeout()
            if (err.message.includes('401') && !toast.isActive('expiredSessionToast')) return expiredSession()
        }
    }
}

export default UsoundPaymentsService
